import { useQuery } from "@apollo/client";

import DocumentationButton from "components/DocumentationButton";
import NoAccessPage from "components/error/NoAccessPage";
import { LogoSlack } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import MetaInfoListItem from "components/MetaInfoList/Item";
import ViewHeaderTitle from "components/ViewHeader/Title";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AuthorizationScheme } from "types/generated";
import { isSaasDistribution, isSelfHostedDistribution } from "utils/distribution";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { useLayoutMeta } from "../../hooks/useLayoutMeta";
import SettingsIntegrationsSlackConnectButton from "./ConnectButton";
import { GET_ACCOUNT_INTEGRATIONS } from "./gql";
import ManageSlackAccess from "./ManageSlackAccess";
import { showManageSlackAccessCreateDrawer } from "./ManageSlackAccess/components/CreateDrawer";
import { UserManagementActivationStatus } from "./ManageSlackAccess/types";
import SettingsIntegrationsSlackTileIndicatorSaaS from "./TileIndicatorSaaS";
import SettingsIntegrationsSlackTileIndicatorSelfHosted from "./TileIndicatorSelfHosted";

const isSelfHosted = isSelfHostedDistribution();
const isSaas = isSaasDistribution();

const SlackIntegration = () => {
  const { viewer } = useTypedContext(AccountContext);

  const { loading, data, error } = useQuery(GET_ACCOUNT_INTEGRATIONS, {
    variables: { isSelfHosted },
  });

  const activationStatus =
    data?.authorizationScheme === AuthorizationScheme.ManagedUsers
      ? UserManagementActivationStatus.ACTIVE
      : UserManagementActivationStatus.INACTIVE;

  const hasIntegration = !!data?.slackIntegration;

  useTitle("Integrations · Slack");

  useLayoutHeading(
    <>
      <ViewHeaderTitle>Slack</ViewHeaderTitle>
      {hasIntegration && <Badge variant="green" type="strong" text="Active" />}
    </>,
    "Slack"
  );

  useLayoutMeta(
    hasIntegration ? (
      <MetaInfoListItem
        href={`https://app.slack.com/apps-manage/${data.slackIntegration?.teamID}/integrations/installed`}
        linkText={data.slackIntegration?.teamName}
        target="_blank"
        icon={LogoSlack}
        tooltip={`Spacelift is already integrated with ${data.slackIntegration?.teamName} workspace. You can disable the integration by removing the Spacelift app from your Slack workspace.`}
        tooltipWidthMode="maxWidthSm"
      />
    ) : null
  );

  useLayoutActions(
    viewer.admin && !loading ? (
      <>
        {hasIntegration && (
          <Button
            onPress={() => showManageSlackAccessCreateDrawer({ activationStatus })}
            variant="primary"
          >
            Grant access
          </Button>
        )}
        {isSaas ? (
          <SettingsIntegrationsSlackTileIndicatorSaaS hasIntegration={hasIntegration} />
        ) : (
          <SettingsIntegrationsSlackTileIndicatorSelfHosted
            hasIntegration={hasIntegration}
            appConfig={data?.slackAppConfig}
          />
        )}
      </>
    ) : null
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (!viewer.admin) {
    return <NoAccessPage />;
  }

  if (loading && !data?.slackIntegration) {
    return <PageLoading />;
  }

  if (!hasIntegration) {
    return (
      <EmptyState
        title="Your Slack integration is not set up yet."
        caption="Many tech companies use Slack for internal communication. As a Spacelift and Slack admin, you can link your Spacelift account to the Slack workspace."
        icon={LogoSlack}
      >
        <Box gap="medium">
          <SettingsIntegrationsSlackConnectButton
            hasIntegration={hasIntegration}
            hasAppConfig={!!data?.slackAppConfig?.clientId}
          />
          <DocumentationButton
            to={getDocsUrl("/integrations/chatops/slack")}
            label="Documentation"
          />
        </Box>
      </EmptyState>
    );
  }

  return <ManageSlackAccess />;
};

export default SlackIntegration;
